import {
  SearchSettingPayload,
  SettingCreateRequestPayload,
  SettingUpdateRequestPayload,
} from "../domain/setting.interface";
import { settingService } from "../services/setting.service";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { parseBoolean } from "./../utils/parseBoolean";
import {
  formatDateForDisplay,
  formatDateTimeForDisplay,
  getDateFormatByCountry,
} from "../utils/dateUtils";
import { Dayjs } from "dayjs";

const { findAll, findByName } = settingService;
const INFINITE = 10000000;

export const useGetSettings = () => {
  const userQuery = useQuery({
    queryKey: ["get-setting-list"],
    queryFn: () => findAll(),
  });

  return userQuery;
};

export const useGetSettingByName = (settingName: string) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["get-setting-by-name", settingName],
    queryFn: () => findByName(settingName),
    staleTime: 10 * 1000, // 10 seconds in milliseconds
  });

  return {
    setting: data,
    isLoadingSetting: isLoading,
    refetchSetting: refetch,
  };
};

export const useCurrencySetting = (): {
  mask: string;
  currency: string;
  default: string;
} => {
  const { setting: currencySetting } = useGetSettingByName("currency");
  const currencySettingObject = currencySetting as {
    currency: {
      mask: string;
      currency: string;
      default: string;
    };
  };

  // Check if currencySettingObject exists and has the new object format
  if (currencySettingObject) {
    return {
      mask: currencySettingObject.currency.mask as string,
      currency: currencySettingObject.currency.currency as string,
      default: currencySettingObject.currency.default as string,
    };
  }

  // Return default if no data or empty body
  return { mask: "£", currency: "£", default: "GBP" };
};

export const useIANATimeZoneSetting = (): string => {
  // Backend uses `timeZone` (camelCase) for the backoffice setting key.
  const { setting: timeZoneSetting } = useGetSettingByName("timeZone");
  return (timeZoneSetting?.timeZone as string) || "Europe/London";
};

export const useCountrySetting = (): string => {
  const { setting: countrySetting } = useGetSettingByName("country");
  return (countrySetting?.country as string) || "US";
};

export const useDateOfBirthSetting = (): string => {
  const { setting: dateOfBirthSetting } = useGetSettingByName("dateOfBirth");
  return (dateOfBirthSetting?.dateOfBirth as string) || "mandatory";
};

export const useEmailSetting = (): boolean => {
  const { setting } = useGetSettingByName("emailMandatory");
  return ((setting?.emailMandatory === "true") as boolean) || false;
};

export const usePhoneSetting = (): boolean => {
  const { setting } = useGetSettingByName("phoneMandatory");
  return ((setting?.phoneMandatory === "true") as boolean) || false;
};

export const useDOBVisualizationSetting = (): boolean => {
  const { setting } = useGetSettingByName("DOBVisualization");
  return ((setting?.DOBVisualization === "true") as boolean) || false;
};

export const useDOBMandatorySetting = (): boolean => {
  const { setting } = useGetSettingByName("DOBMandatory");
  return ((setting?.DOBMandatory === "true") as boolean) || false;
};

export const useColorModuleSetting = (): boolean => {
  const { setting: coloringModuleSetting } =
    useGetSettingByName("coloringModule");
  return (coloringModuleSetting?.coloringModule as boolean) || false;
};

export const useColoringRequiredSetting = (): boolean => {
  const { setting: coloringRequiredSetting } = useGetSettingByName(
    "coloringParticipantRequired",
  );
  return (
    (coloringRequiredSetting?.coloringParticipantRequired as boolean) || false
  );
};

export const useMaxOuSetting = (): number => {
  const { setting } = useGetSettingByName("maxNumberOUs");

  if (
    setting?.maxNumberOUs === null ||
    setting?.maxNumberOUs === undefined ||
    setting?.maxNumberOUs === "null"
  ) {
    return INFINITE;
  }

  return (setting?.maxNumberOUs as number) ?? INFINITE;
};

export const useAllowsCustomRoles = (): boolean => {
  const { setting } = useGetSettingByName("allowsCustomRoles");
  return parseBoolean(setting?.allowsCustomRoles);
};

export const useMaxNumberPrograms = (): number => {
  const { setting } = useGetSettingByName("maxNumberPrograms");

  if (
    setting?.maxNumberPrograms === null ||
    setting?.maxNumberPrograms === undefined ||
    setting?.maxNumberPrograms === "null"
  ) {
    return INFINITE;
  }

  return (setting?.maxNumberPrograms as number) ?? INFINITE;
};

export const useHelpDeskPhoneNumber = (): string => {
  const { setting } = useGetSettingByName("helpDeskPhoneNumber");
  return (setting?.helpDeskPhoneNumber as string) || "";
};

export const useHelpFAQURL = (): string => {
  const { setting } = useGetSettingByName("helpFAQURL");
  return (setting?.helpFAQURL as string) || "";
};

export const useHelpDeskHours = (): string => {
  const { setting } = useGetSettingByName("helpDeskHours");
  return (setting?.helpDeskHours as string) || "";
};

export const useHelpDocumentationURL = (): string => {
  const { setting } = useGetSettingByName("helpDocumentationURL");
  return (setting?.helpDocumentationURL as string) || "";
};

export const useHelpCreateTicketEmail = (): string => {
  const { setting } = useGetSettingByName("helpCreateTicketEmail");
  return (setting?.helpCreateTicketEmail as string) || "";
};

export const useDisplayContactFooter = (): boolean => {
  const { setting } = useGetSettingByName("displayContactFooter");
  return (setting?.displayContactFooter as boolean) || false;
};

export const useDisplayDashboard = (): boolean => {
  const { setting } = useGetSettingByName("displaydashboard");
  return (setting?.displaydashboard as boolean) ?? false;
};

export const useMinAgeAllowed = (): number => {
  const { setting } = useGetSettingByName("minAgeAllowed");
  if (
    setting?.minAgeAllowed === null ||
    setting?.minAgeAllowed === undefined ||
    setting?.minAgeAllowed === "null"
  ) {
    return 18; // Default minimum age
  }
  return (setting?.minAgeAllowed as number) ?? 18;
};

export const useClientIdSetting = (): string => {
  const { setting } = useGetSettingByName("backofficeParticipantClientId");
  return (setting?.backofficeParticipantClientId as string) || "optional";
};

export const useBackofficeParticipantPhoneNumberSetting = (): string => {
  const { setting } = useGetSettingByName("backofficeParticipantPhoneNumber");
  return (setting?.backofficeParticipantPhoneNumber as string) || "optional";
};

export const useBackofficeParticipantDateOfBirthSetting = (): string => {
  const { setting } = useGetSettingByName("backofficeParticipantDateOfBirth");
  return (setting?.backofficeParticipantDateOfBirth as string) || "optional";
};

export const useBackofficeParticipantEmailSetting = (): string => {
  const { setting } = useGetSettingByName("backofficeParticipantEmail");
  return (setting?.backofficeParticipantEmail as string) || "optional";
};

// CRUD Hooks for Settings Management
const { search, create, update, softDelete } = settingService;

export const useSettings = (params: SearchSettingPayload) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["settings-search", params],
    queryFn: () => search(params),
    staleTime: 5000,
  });

  return {
    settings: data?.settings || [],
    totalCount: data?.totalCount || 0,
    isLoadingSettings: isLoading,
    refetchSettings: refetch,
  };
};

export const useSettingCreateOne = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SettingCreateRequestPayload) => create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings-search"] });
      queryClient.invalidateQueries({ queryKey: ["get-setting-list"] });
    },
  });
};

export const useSettingUpdateOne = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: SettingUpdateRequestPayload;
    }) => update({ id, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings-search"] });
      queryClient.invalidateQueries({ queryKey: ["get-setting-list"] });
    },
  });
};

export const useSettingDeleteOne = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => softDelete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings-search"] });
      queryClient.invalidateQueries({ queryKey: ["get-setting-list"] });
    },
  });
};

/**
 * Hook for locale-aware date formatting based on country settings
 *
 * Returns date formatting functions that automatically use the account's
 * country setting to format dates appropriately.
 *
 * @returns Object with date formatting functions and format string
 *
 * @example
 * const { formatDate, formatDateTime, dateFormat } = useDateFormatter();
 *
 * // For US (country setting = "US"):
 * formatDate("2026-02-18") // → "02/18/2026"
 * formatDateTime("2026-02-18T15:30:00Z") // → "02/18/2026 03:30 PM"
 * dateFormat // → "MM/DD/YYYY"
 *
 * // For GB (country setting = "GB"):
 * formatDate("2026-02-18") // → "18/02/2026"
 * formatDateTime("2026-02-18T15:30:00Z") // → "18/02/2026 03:30 PM"
 * dateFormat // → "DD/MM/YYYY"
 */
export const useDateFormatter = () => {
  const countryCode = useCountrySetting();

  return {
    /**
     * Format a date for display according to country settings
     * @param date - Date to format (Date, Dayjs, or ISO string)
     * @returns Formatted date string (e.g., "02/18/2026" for US, "18/02/2026" for GB)
     */
    formatDate: (date: Date | Dayjs | string | null | undefined): string => {
      return formatDateForDisplay(date, countryCode);
    },

    /**
     * Format a datetime for display according to country settings
     * @param date - Date to format
     * @returns Formatted datetime string with time (e.g., "02/18/2026 3:30 PM")
     */
    formatDateTime: (
      date: Date | Dayjs | string | null | undefined,
    ): string => {
      return formatDateTimeForDisplay(date, countryCode);
    },

    /**
     * Get the date format string for this country (useful for date pickers)
     * @returns Format string (e.g., "MM/DD/YYYY" for US, "DD/MM/YYYY" for GB)
     */
    dateFormat: getDateFormatByCountry(countryCode),

    /**
     * The current country code
     */
    countryCode,
  };
};
